import { useEffect, useId, useRef, useState } from "react";
import type { LeadCardResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMe } from "../../api/queries";
import { useOptionalDialerSession } from "../../dialer/dialer-session";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { formatNumericDateTime } from "../../i18n/format";
import { useT } from "../../i18n/locale";
import { initialsInTab, nameInTab, useAccess } from "../../session/access";
import { tabKey, type TabContext } from "../../session/tab-context";
import { isCallLive, numberDiagnosisOf, useCallSession, type CallSession } from "../../telephony/call-session";
import { callErrorText, needsReload } from "../../telephony/call-texts";
import { ringDurationText } from "../../telephony/call-display";
import { reloadPage } from "../../telephony/reload-page";
import { openCallQuery } from "../../telephony/telephony-queries";
import type { LeadCardWrites } from "../lead-queries";
import { AudioDevicesControl } from "./AudioDevices";
import { OutcomeSteps } from "./OutcomeSteps";
import "./call-desk.css";

// La «Postazione di chiamata» della scheda Lead (piano M4, Task 13, mock-up `scheda-lead.html`, sopra la Timeline): in
// alto il nome di chi chiama (mai il numero che vede il Lead, decisione del 25 settembre 2026); poi «Pronto…», la chiamata
// con «In chiamata», il tempo e «Chiudi la chiamata», l'esito con i passi dopo (`OutcomeSteps.tsx`), oppure perché la
// chiamata non è partita (se il telefono del browser non si è caricato, con «Ricarica la pagina»). La chiamata è della
// sessione del guscio (`telephony/call-session.tsx`): uscendo dalla scheda continua, e tornando la postazione la mostra
// com'è. Una chiamata di chi guarda finita senza esito (la pagina chiusa, un nuovo accesso) torna qui con «Manca l'esito
// della chiamata delle …» finché l'esito non c'è (questione 161).
// Nella cascata del Power Dialer (piano M5, Task 5), fatti l'esito e i passi obbligatori del Lead aperto, la postazione dice
// «Fatto tutto: passa al Lead successivo…»: il pulsante sta soltanto nella barra in alto. Nella cascata, una chiamata a cui non
// ha risposto nessuno dice «Senza risposta: Gioia365 segna «Nessuna risposta».» finché l'esito di Gioia365 non arriva (TEL-32), con
// «Scegli l'esito», che chiude l'attesa e mostra subito il passo dell'esito di sempre (ruling S3: l'attesa non blocca).
// Accanto a chi chiama, il microfono in uso, che si cambia anche durante la chiamata (`AudioDevices.tsx`, richiesta del
// product owner del 6 ottobre 2026); il suggerimento sul microfono del computer soltanto dove la postazione chiama o segue
// una chiamata.

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  leadName: string;
  writes: LeadCardWrites;
  onCardError: (error: unknown) => void;
  timeZone: string;
};

/** La sessione riguarda questo Lead, nella stessa azienda della scheda. */
function sessionOf(session: CallSession, tab: TabContext, leadId: string): Exclude<CallSession, { phase: "idle" }> | null {
  if (session.phase === "idle" || session.target === null) return null;
  return session.target.leadId === leadId && tabKey(session.target.tab) === tabKey(tab) ? session : null;
}

export function CallDesk({ tab, card, leadName, writes, onCardError, timeZone }: Props) {
  const t = useT();
  const me = useMe();
  const access = useAccess();
  const titleId = useId();
  const api = useCallSession();
  const dialer = useOptionalDialerSession();
  const leadId = card.lead.id;
  const here = sessionOf(api.session, tab, leadId);
  // La chiamata di chi guarda ancora senza esito: soltanto con un'appartenenza, fuori da «Guarda come…» (l'esito lo dà
  // l'autore, mai la persona osservata).
  const askOpenCall = tab.kind === "membership" && tab.commercialView === undefined;
  // Mentre la scheda del browser segue una chiamata sua, la chiamata aperta non si rilegge da sola: la segue la sessione.
  const open = useQuery({ ...openCallQuery(tab, leadId, { poll: !isCallLive(api.session) }), enabled: askOpenCall });
  const missing = open.data?.needsOutcome ? open.data : null;
  const { resume } = api;
  const idle = api.session.phase === "idle";
  useEffect(() => {
    if (missing && idle) resume({ tab, call: missing, leadName });
  }, [missing, idle, resume, tab, leadName]);
  // «Scegli l'esito» sparisce con il clic: il focus passa alla prima scelta dell'esito, che compare al suo posto (TEL-32).
  const bodyRef = useRef<HTMLDivElement>(null);
  const focusChoice = useRef(false);
  useEffect(() => {
    if (!focusChoice.current) return;
    const first = bodyRef.current?.querySelector<HTMLInputElement>('input[type="radio"]');
    if (!first) return;
    focusChoice.current = false;
    first.focus();
  });

  // Il Lead aperto della cascata con l'esito e i passi fatti (anche a postazione chiusa).
  const dialerPhase = dialer?.browser?.phase === "paused" ? dialer.browser.pausedFrom : dialer?.browser?.phase;
  const allDone = dialer?.session?.current?.id === leadId && dialerPhase === "done";
  const callable = card.permissions.canCall && card.calling.callable;
  if (!here && !callable && !allDone) return null;

  const callerName = nameInTab(me, access);

  let body;
  if (!here) {
    body = <p className="g-desk__ready">{allDone ? t("dialer.allDone") : t("callDesk.ready")}</p>;
  } else if (isCallLive(here)) {
    body = (
      <>
        <div className="g-desk__row">
          <span className="g-desk__live" role="status">
            {t(here.phase === "ringing" ? "callDesk.ringing" : "callDesk.live")}
          </span>
          <CallTimer startedAt={here.phase === "starting" ? null : here.startedAt} />
          <span className="g-desk__spacer" />
          <Button onClick={api.hangUp}>
            <Icon name="hangup" />
            {t("callDesk.hangUp")}
          </Button>
        </div>
        <p className="g-desk__hint">{t("callDesk.leaveWarning")}</p>
      </>
    );
  } else if (here.phase === "ending") {
    body = <p className="g-desk__ready" role="status">{t("callDesk.ending")}</p>;
  } else if (here.phase === "outcome" && numberDiagnosisOf(here.call) && here.save.state !== "saved") {
    // La diagnosi del fornitore su una chiamata che non ha contato (TEL-10): «Controllo dopo» la lascia com'è; nel Power Dialer
    // vale come una chiamata non arrivata al Lead, con il conto alla rovescia in pausa (`dialer-session.tsx`).
    const saving = here.save.state === "saving";
    body = <div className="g-desk__problem">
      <Reason tone="warning">{t(numberDiagnosisOf(here.call) === "unreachable" ? "callDesk.unreachableNumber" : "callDesk.invalidNumber")}</Reason>
      <p>{ringDurationText(t, here.call.quality)}</p>
      <Button disabled={saving} onClick={() => void api.saveOutcome({ outcome: "wrong_number", answeredOutcome: null, detail: null, note: null }).catch(onCardError)}>{t("callDesk.markWrongNumber")}</Button>
      <Button variant="ghost" disabled={saving} onClick={() => api.close()}>{t("callDesk.numberLater")}</Button>
    </div>;
  } else if (here.phase === "outcome" && dialer?.awaitingOutcome === here.call.id) {
    // Una chiamata della cascata a cui non ha risposto nessuno: l'esito lo dà Gioia365 (TEL-32), e la postazione lo aspetta.
    const callId = here.call.id;
    body = (
      <div className="g-desk__row">
        <p className="g-desk__ready" role="status">
          {t("dialer.noAnswerWaiting")}
        </p>
        <span className="g-desk__spacer" />
        <Button
          onClick={() => {
            focusChoice.current = true;
            dialer.chooseOutcome(callId);
          }}
        >
          {t("dialer.chooseOutcome")}
        </Button>
      </div>
    );
  } else if (here.phase === "outcome") {
    body = (
      <>
        {here.missing ? <Reason tone="warning">{t("callDesk.missingOutcome", { time: formatNumericDateTime(timeZone, here.call.startedAt) })}</Reason> : null}
        {here.notice ? <Reason tone="warning">{callErrorText(t, here.notice)}</Reason> : null}
        <OutcomeSteps key={here.call.id} tab={tab} card={card} session={here} writes={writes} onCardError={onCardError} timeZone={timeZone} />
        {allDone ? <Reason>{t("dialer.allDone")}</Reason> : null}
      </>
    );
  } else {
    body = (
      <div className="g-desk__problem">
        {here.call?.quality ? <p>{ringDurationText(t, here.call.quality)}</p> : null}
        <Reason tone="warning" role="alert">
          {callErrorText(t, here.error)}
        </Reason>
        {needsReload(here.error) ? (
          <Button variant="primary" onClick={reloadPage}>
            {t("callDesk.reload")}
          </Button>
        ) : null}
        <Button variant="ghost" onClick={api.dismiss}>
          {t("callDesk.dismiss")}
        </Button>
      </div>
    );
  }

  return (
    <section className="g-desk" aria-labelledby={titleId} data-phase={here?.phase ?? "idle"}>
      <div className="g-desk__head">
        <h2 id={titleId} className="g-desk__title">
          <Icon name="phone" />
          {t("callDesk.title")}
        </h2>
        <p className="g-desk__caller">
          <span className="g-avatar" aria-hidden="true">
            {initialsInTab(me, access)}
          </span>
          <span aria-hidden="true">{callerName}</span>
          <span className="g-visually-hidden">{t("callDesk.callerLabel", { name: callerName })}</span>
        </p>
        <AudioDevicesControl hint={callable || here !== null} />
      </div>
      <div ref={bodyRef} className="g-desk__body">
        {body}
      </div>
    </section>
  );
}

/** Il tempo della chiamata, da quando squilla: minuti e secondi, che scorrono ogni secondo. */
function CallTimer({ startedAt }: { startedAt: number | null }) {
  const t = useT();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [startedAt]);
  const seconds = startedAt === null ? 0 : Math.max(0, Math.floor((now - startedAt) / 1_000));
  const text = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  return (
    <span className="g-desk__timer" role="timer" aria-label={t("callDesk.timer")}>
      {text}
    </span>
  );
}
